/**
 * Determines if the current navigation entry is "fresh". The function
 * returns true if the entry is a reload or a visit from an external referrer
 * and otherwise returns false.
 * @returns {boolean} True if the entry is fresh.
 */
export function isFreshNavigation() {
  if ("getEntriesByType" in (window.performance || {})) {
    const entries = window.performance
      .getEntriesByType("navigation")
      .map((o) => o.type);
    if (entries.includes("reload")) {
      return true;
    }
    if (entries.includes("back_forward")) {
      return false;
    }
    const { referrer } = document;
    if (!referrer?.startsWith(window.location.origin)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks if the user has indicated a preference for reduced motion.
 * @returns {boolean} True if the user prefers reduced motion.
 */
export const prefersReducedMotion = (() => {
  const media =
    "matchMedia" in window
      ? window.matchMedia("prefers-reduced-motion")
      : { matches: false };
  return () => media.matches;
})();

/**
 * Returns an random number, clamped by the provided minimum and maximum.
 * @param {number} [min] The minimum returned value.
 * @param {number} [max] The maximum returned value.
 * @returns {number} A random number.
 */
export function random(min = 0, max = 1) {
  return Math.random() * (max - min) + min;
}

/**
 * Rounds the provdided number to the nearest integer number or the provided
 * nunber of decimal places.
 * @param {number} n The number to round.
 * @param {number} [d] The maximum number of decimal places.
 * @returns {number} The rounded number.
 */
export function round(n, d = 0) {
  return Math.round(n * 10 ** d) / 10 ** d;
}

/**
 * Returns a shuffled copy of the provided array. Does not alter the array.
 * @template T
 * @param {T[]} arr The array to shuffle.
 * @returns {T[]} A shuffled copy of the provided array.
 */
export function shuffle(arr) {
  if (!Array.isArray(arr)) {
    throw new Error(`shuffle() expected an array; received ${typeof arr}`);
  }
  if (arr.length < 2) {
    return arr;
  }
  const result = arr.slice();
  let i = arr.length - 1;
  while (i >= 0) {
    const rand = Math.floor(random(0, i + 1));
    const temp = result[i];
    result[i] = result[rand];
    result[rand] = temp;
    i -= 1;
  }
  return result;
}
